/**
 * A client's asset (CMDB) schemas (RESTORE_PLAN R1.8): install a starter template, read the
 * installed list back from the server, remove one. Server rules:
 * supabase/functions/server/assetSchemaCore.ts.
 */
import { projectId } from './supabase/info';

const BASE = `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/asset-schemas`;

export interface InstalledAssetObjectType {
  id: string;
  name: string;
  description?: string;
  attributes?: unknown[];
  discoveryMapping?: { source: string; sourceTable?: string; deduplicationKey: string };
}

export interface InstalledAssetSchema {
  id: string;
  templateId: string;
  sourceTemplate: string;
  complexity?: string;
  name: string;
  description?: string;
  objectTypes: InstalledAssetObjectType[];
  installedAt: string;
  installedBy?: { id: string; name?: string };
}

async function send<T>(url: string, accessToken: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(url, {
    ...init,
    headers: { Authorization: `Bearer ${accessToken}`, ...(init.body ? { 'Content-Type': 'application/json' } : {}) },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Request failed (${res.status})`);
  }
  return res.json() as Promise<T>;
}

export async function loadInstalledSchemas(companyId: string, accessToken: string): Promise<InstalledAssetSchema[]> {
  const data = await send<{ schemas?: InstalledAssetSchema[] }>(`${BASE}/installed/${encodeURIComponent(companyId)}`, accessToken);
  return data.schemas || [];
}

export async function installSchemaTemplate(companyId: string, templateId: string, accessToken: string): Promise<InstalledAssetSchema[]> {
  const data = await send<{ schemas?: InstalledAssetSchema[] }>(`${BASE}/install/${encodeURIComponent(companyId)}`, accessToken, {
    method: 'POST',
    body: JSON.stringify({ templateId }),
  });
  return data.schemas || [];
}

export async function removeInstalledSchema(companyId: string, schemaId: string, accessToken: string): Promise<InstalledAssetSchema[]> {
  const data = await send<{ schemas?: InstalledAssetSchema[] }>(`${BASE}/installed/${encodeURIComponent(companyId)}/${encodeURIComponent(schemaId)}`, accessToken, { method: 'DELETE' });
  return data.schemas || [];
}

/** "Maps from Lansweeper via Serial Number", for an object type that has a discovery mapping. */
export function discoveryMappingText(ot: InstalledAssetObjectType): string | null {
  const m = ot.discoveryMapping;
  if (!m?.source) return null;
  const source = m.source.charAt(0).toUpperCase() + m.source.slice(1);
  return `Maps from ${source} via ${m.deduplicationKey}`;
}
